import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { Camera, FileText, PenTool, FolderOpen, ArrowUp, ArrowDown, Download, Mic, Video, X as XIcon } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { useRole } from '../hooks/useRole';
import { ThemedText, MarkdownPreviewModal } from '../components';
import { storageService } from '../api';
import { StyledSelect as Select } from '../components/FormStyles';

// --- Helpers ---

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const val = bytes / Math.pow(1024, i);
  return `${val.toFixed(i > 1 ? 1 : 0)} ${units[i]}`;
};

const fmtDate = (d) => {
  if (!d) return '--';
  return new Date(d).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const getFileIcon = (attachmentType) => {
  switch (attachmentType) {
    case 'Image': return <Camera size={16} />;
    case 'Document': return <FileText size={16} />;
    case 'Voice': return <Mic size={16} />;
    case 'Video': return <Video size={16} />;
    case 'Drawing': return <PenTool size={16} />;
    default: return <FolderOpen size={16} />;
  }
};

const isImageType = (attachmentType, contentType) => {
  if (attachmentType === 'Image') return true;
  if (contentType && contentType.startsWith('image/')) return true;
  return false;
};

const FILE_TYPES = ['All', 'Image', 'Document', 'Voice', 'Video', 'Drawing', 'Generated Report'];

// --- Styled Components ---

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: ${p => p.theme.spacing.xl};
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const SummaryCard = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.lg};
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${p => p.theme.colors.text};
  margin-bottom: 4px;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const FiltersRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.lg};
  flex-wrap: wrap;
  align-items: center;
`;

const SearchInput = styled.input`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  min-width: 250px;
  outline: none;
  transition: border-color 0.2s ease;
  &:focus {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  }
  &::placeholder {
    color: ${p => p.theme.colors.muted};
  }
`;

const TableContainer = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border-bottom: 2px solid ${p => p.theme.colors.border};
`;

const Th = styled.th`
  text-align: left;
  padding: ${p => p.theme.spacing.md};
  font-weight: 600;
  color: ${p => p.theme.colors.text};
  font-size: 13px;
  white-space: nowrap;
  cursor: ${p => p.sortable ? 'pointer' : 'default'};
  user-select: none;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${p => p.sortable ? p.theme.colors.background : 'transparent'};
  }
`;

const SortIndicator = styled.span`
  margin-left: 4px;
  color: ${p => p.theme.colors.primary || p.theme.colors.tint};
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${p => p.theme.colors.border};
  transition: background-color 0.2s ease;
  cursor: pointer;
  &:hover { background-color: ${p => p.theme.colors.background}; }
  &:last-child { border-bottom: none; }
`;

const Td = styled.td`
  padding: ${p => p.theme.spacing.md};
  color: ${p => p.theme.colors.text};
  font-size: 13px;
`;

const FileNameCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
`;

const FileIcon = styled.span`
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
`;

const Thumbnail = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  background-color: ${p => p.theme.colors.background};
`;

const FileName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background-color: ${p => p.bg || '#3b82f620'};
  color: ${p => p.color || '#3b82f6'};
`;

const SubmissionLink = styled.span`
  color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  cursor: pointer;
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const DownloadButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  padding: ${p => p.theme.spacing.xs};
  border-radius: ${p => p.theme.borderRadius.sm};
  transition: background-color 0.2s ease;
  &:hover {
    background-color: ${p => p.theme.colors.border};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${p => p.theme.spacing.md};
  border-top: 1px solid ${p => p.theme.colors.border};
  flex-wrap: wrap;
  gap: ${p => p.theme.spacing.sm};
`;

const PageInfo = styled.span`
  font-size: 13px;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
`;

const PageButtons = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.xs};
`;

const PageButton = styled.button`
  padding: ${p => p.theme.spacing.xs} ${p => p.theme.spacing.sm};
  border: 1px solid ${p => p.active ? (p.theme.colors.primary || p.theme.colors.tint) : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.sm};
  background-color: ${p => p.active ? (p.theme.colors.primary || p.theme.colors.tint) : p.theme.colors.background};
  color: ${p => p.active ? '#fff' : p.theme.colors.text};
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover:not(:disabled) {
    border-color: ${p => p.theme.colors.primary || p.theme.colors.tint};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AccessDenied = styled.div`
  text-align: center;
  padding: ${p => p.theme.spacing.xl};
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${p => p.theme.spacing.lg};
`;

const ModalContent = styled.div`
  background-color: ${p => p.theme.colors.backgroundSecondary};
  border-radius: ${p => p.theme.borderRadius.lg};
  max-width: 800px;
  max-height: 90vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${p => p.theme.spacing.lg};
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const ModalTitle = styled.h3`
  margin: 0;
  color: ${p => p.theme.colors.text};
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 600px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
  padding: 0;
  line-height: 1;
  &:hover {
    color: ${p => p.theme.colors.text};
  }
`;

const ModalBody = styled.div`
  padding: ${p => p.theme.spacing.lg};
  overflow-y: auto;
  flex: 1;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 400px;
  display: block;
  margin: 0 auto ${p => p.theme.spacing.lg};
  border-radius: ${p => p.theme.borderRadius.md};
  background-color: ${p => p.theme.colors.background};
`;

const PreviewPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  background-color: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  margin-bottom: ${p => p.theme.spacing.lg};
  font-size: 48px;
`;

const FileDetails = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const DetailLabel = styled.span`
  color: ${p => p.theme.colors.textSecondary || p.theme.colors.muted};
  font-size: 13px;
`;

const DetailValue = styled.span`
  color: ${p => p.theme.colors.text};
  font-size: 13px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${p => p.theme.spacing.md};
  padding: ${p => p.theme.spacing.lg};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Button = styled.button`
  padding: ${p => p.theme.spacing.sm} ${p => p.theme.spacing.lg};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  ${p => p.primary ? `
    background-color: ${p.theme.colors.primary || p.theme.colors.tint};
    color: white;
    border: none;
    &:hover {
      opacity: 0.9;
    }
  ` : `
    background-color: ${p.theme.colors.background};
    color: ${p.theme.colors.text};
    border: 1px solid ${p.theme.colors.border};
    &:hover {
      background-color: ${p.theme.colors.border};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Type badge colors
const TYPE_COLORS = {
  Image: { color: '#22c55e', bg: '#22c55e20' },
  Document: { color: '#3b82f6', bg: '#3b82f620' },
  Voice: { color: '#f59e0b', bg: '#f59e0b20' },
  Video: { color: '#8b5cf6', bg: '#8b5cf620' },
  Drawing: { color: '#ec4899', bg: '#ec489920' },
  'Generated Report': { color: '#0ea5e9', bg: '#0ea5e920' },
};

// --- Component ---

export const FileManagerSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const hasAdminAccess = isAdmin();

  const [files, setFiles] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');

  // Sorting
  const [sortField, setSortField] = useState('uploadedAt');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Preview modal
  const [previewFile, setPreviewFile] = useState(null);
  const [markdownPreview, setMarkdownPreview] = useState(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!hasAdminAccess) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const [usageRes, filesRes] = await Promise.all([
          storageService.getStorageUsage(),
          storageService.listFiles(null, debouncedSearch || null, page, pageSize, sortField, sortDir),
        ]);

        setUsage(usageRes?.data || usageRes);

        const filesData = filesRes?.data || filesRes;
        setFiles(filesData?.items || filesData?.files || []);
        setTotalCount(filesData?.totalCount || filesData?.total || 0);
      } catch (err) {
        console.error('Failed to fetch files:', err);
        toast.error('Failed to load files');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [debouncedSearch, page, pageSize, sortField, sortDir, hasAdminAccess]);

  // Filter files by type (client-side since API might not support type filter yet)
  const filteredFiles = typeFilter === 'All'
    ? files
    : typeFilter === 'Generated Report'
      ? files.filter(f => f.category === 'generated-report')
      : files.filter(f => f.attachmentType === typeFilter || f.type === typeFilter);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1); // Reset to first page when sort changes
  };

  const handleRowClick = (file) => {
    const name = file.fileName || file.name;
    if (name?.toLowerCase().endsWith('.md') && file.fileUrl) {
      setMarkdownPreview({ fileUrl: file.fileUrl, fileName: name });
    } else {
      setPreviewFile(file);
    }
  };

  const handleDownload = async (file, e) => {
    if (e) {
      e.stopPropagation();
    }

    const blobPath = file.blobPath || file.path;
    if (!blobPath) {
      toast.error('File path not available');
      return;
    }

    try {
      setDownloadingFile(file.id);
      const result = await storageService.getDownloadUrl(blobPath);
      const url = result?.downloadUrl || result?.data?.downloadUrl || result?.url || result?.data?.url;

      if (url) {
        window.open(url, '_blank');
      } else {
        toast.error('Failed to generate download link');
      }
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to generate download link');
    } finally {
      setDownloadingFile(null);
    }
  };

  const handleSubmissionClick = (file, e) => {
    e.stopPropagation();
    if (file.formSchemaId && file.formSubmissionId) {
      navigate(`/forms/${file.formSchemaId}/submissions/${file.formSubmissionId}`);
    }
  };

  const closePreviewModal = () => {
    setPreviewFile(null);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const startItem = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const endItem = Math.min(page * pageSize, totalCount);

  // Calculate type breakdown from usage data or files
  const getTypeCounts = () => {
    // Check for breakdownByType from the API (Dictionary<AttachmentType, StorageTypeUsage>)
    if (usage?.breakdownByType) {
      const counts = {};
      Object.entries(usage.breakdownByType).forEach(([type, data]) => {
        counts[type] = data.fileCount || 0;
      });
      return counts;
    }
    // Fallback: count from current files
    const counts = {};
    files.forEach(f => {
      const type = f.attachmentType || f.type || 'Other';
      counts[type] = (counts[type] || 0) + 1;
    });
    return counts;
  };

  const typeCounts = getTypeCounts();

  // Access guard - isAdmin() checks for both SuperAdmin and OrgAdmin
  if (!hasAdminAccess) {
    return (
      <>
        <PageContainer theme={theme}>
          <AccessDenied theme={theme}>
            <ThemedText type="title">Access Denied</ThemedText>
            <ThemedText>You do not have permission to access this page.</ThemedText>
          </AccessDenied>
        </PageContainer>
      </>
    );
  }

  const renderSortIndicator = (field) => {
    if (sortField !== field) return null;
    return <SortIndicator theme={theme}>{sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />}</SortIndicator>;
  };

  return (
    <>
      <PageContainer theme={theme}>
        <PageHeader theme={theme}>
          <ThemedText type="title">File Manager</ThemedText>
          <ThemedText type="subtitle">Browse and manage your organization's uploaded files</ThemedText>
        </PageHeader>

        {/* Summary Cards */}
        <SummaryCards theme={theme}>
          <SummaryCard theme={theme}>
            <SummaryValue theme={theme}>
              {usage?.totalFiles ?? totalCount}
            </SummaryValue>
            <SummaryLabel theme={theme}>Total Files</SummaryLabel>
          </SummaryCard>
          <SummaryCard theme={theme}>
            <SummaryValue theme={theme}>
              {formatBytes(usage?.totalStorageBytes || usage?.totalSizeBytes || usage?.currentBytes || 0)}
            </SummaryValue>
            <SummaryLabel theme={theme}>Total Size</SummaryLabel>
          </SummaryCard>
          <SummaryCard theme={theme}>
            <SummaryValue theme={theme}>
              {typeCounts.Image || 0}
            </SummaryValue>
            <SummaryLabel theme={theme}>Images</SummaryLabel>
          </SummaryCard>
          <SummaryCard theme={theme}>
            <SummaryValue theme={theme}>
              {typeCounts.Document || 0}
            </SummaryValue>
            <SummaryLabel theme={theme}>Documents</SummaryLabel>
          </SummaryCard>
        </SummaryCards>

        {/* Filters */}
        <FiltersRow theme={theme}>
          <SearchInput
            theme={theme}
            type="text"
            placeholder="Search files..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <Select
            theme={theme}
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          >
            {FILE_TYPES.map(type => (
              <option key={type} value={type}>{type === 'All' ? 'All Types' : type}</option>
            ))}
          </Select>
        </FiltersRow>

        {/* Files Table */}
        <TableContainer theme={theme}>
          {loading ? (
            <LoadingState theme={theme}>
              <ThemedText>Loading files...</ThemedText>
            </LoadingState>
          ) : filteredFiles.length === 0 ? (
            <EmptyState theme={theme}>
              <ThemedText>
                {searchTerm || typeFilter !== 'All'
                  ? 'No files match your filters'
                  : 'No files uploaded yet'}
              </ThemedText>
            </EmptyState>
          ) : (
            <>
              <Table>
                <Thead theme={theme}>
                  <tr>
                    <Th
                      theme={theme}
                      sortable
                      onClick={() => handleSort('fileName')}
                    >
                      Name{renderSortIndicator('fileName')}
                    </Th>
                    <Th
                      theme={theme}
                      sortable
                      onClick={() => handleSort('attachmentType')}
                    >
                      Type{renderSortIndicator('attachmentType')}
                    </Th>
                    <Th
                      theme={theme}
                      sortable
                      onClick={() => handleSort('fileSize')}
                    >
                      Size{renderSortIndicator('fileSize')}
                    </Th>
                    <Th
                      theme={theme}
                      sortable
                      onClick={() => handleSort('uploadedAt')}
                    >
                      Uploaded{renderSortIndicator('uploadedAt')}
                    </Th>
                    <Th theme={theme}>By</Th>
                    <Th theme={theme}>Submission</Th>
                    <Th theme={theme} style={{ width: '50px' }}></Th>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredFiles.map(file => {
                    const fileType = file.category === 'generated-report' ? 'Generated Report' : (file.attachmentType || file.type || 'Unknown');
                    const typeStyle = TYPE_COLORS[fileType] || { color: '#6b7280', bg: '#6b728020' };
                    const showThumbnail = isImageType(fileType, file.contentType) && file.fileUrl;

                    return (
                      <Tr key={file.id || file.blobPath} theme={theme} onClick={() => handleRowClick(file)}>
                        <Td theme={theme}>
                          <FileNameCell theme={theme}>
                            {showThumbnail ? (
                              <Thumbnail
                                theme={theme}
                                src={file.fileUrl}
                                alt={file.fileName || file.name}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <FileIcon>{getFileIcon(fileType)}</FileIcon>
                            )}
                            <FileName title={file.fileName || file.name}>
                              {file.fileName || file.name || 'Unknown'}
                            </FileName>
                          </FileNameCell>
                        </Td>
                        <Td theme={theme}>
                          <Badge bg={typeStyle.bg} color={typeStyle.color}>
                            {fileType}
                          </Badge>
                        </Td>
                        <Td theme={theme}>
                          {formatBytes(file.fileSize || file.fileSizeBytes || file.size || 0)}
                        </Td>
                        <Td theme={theme}>
                          {fmtDate(file.createdAt || file.uploadedAt)}
                        </Td>
                        <Td theme={theme}>
                          {file.uploadedByName || file.uploadedBy || file.userName || '--'}
                        </Td>
                        <Td theme={theme}>
                          {file.formSubmissionId ? (
                            <SubmissionLink
                              theme={theme}
                              onClick={(e) => handleSubmissionClick(file, e)}
                            >
                              #{file.formSubmissionId}
                            </SubmissionLink>
                          ) : '--'}
                        </Td>
                        <Td theme={theme}>
                          <DownloadButton
                            theme={theme}
                            onClick={(e) => handleDownload(file, e)}
                            disabled={downloadingFile === file.id}
                            title="Download file"
                          >
                            {downloadingFile === file.id ? '...' : <Download size={14} />}
                          </DownloadButton>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <Pagination theme={theme}>
                  <PageInfo theme={theme}>
                    Showing {startItem}-{endItem} of {totalCount} files
                  </PageInfo>
                  <PageButtons theme={theme}>
                    <PageButton
                      theme={theme}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Prev
                    </PageButton>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <PageButton
                          key={pageNum}
                          theme={theme}
                          active={page === pageNum}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </PageButton>
                      );
                    })}
                    <PageButton
                      theme={theme}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </PageButton>
                  </PageButtons>
                </Pagination>
              )}
            </>
          )}
        </TableContainer>

        {/* Preview Modal */}
        {previewFile && (
          <ModalOverlay theme={theme} onClick={closePreviewModal}>
            <ModalContent theme={theme} onClick={e => e.stopPropagation()}>
              <ModalHeader theme={theme}>
                <ModalTitle theme={theme}>{previewFile.fileName || previewFile.name}</ModalTitle>
                <CloseButton theme={theme} onClick={closePreviewModal}><XIcon size={18} /></CloseButton>
              </ModalHeader>
              <ModalBody theme={theme}>
                {isImageType(previewFile.attachmentType || previewFile.type, previewFile.contentType) && previewFile.fileUrl ? (
                  <PreviewImage
                    theme={theme}
                    src={previewFile.fileUrl}
                    alt={previewFile.fileName || previewFile.name}
                  />
                ) : (
                  <PreviewPlaceholder theme={theme}>
                    {getFileIcon(previewFile.attachmentType || previewFile.type)}
                  </PreviewPlaceholder>
                )}
                <FileDetails theme={theme}>
                  <DetailLabel theme={theme}>Type:</DetailLabel>
                  <DetailValue theme={theme}>{previewFile.attachmentType || previewFile.type || 'Unknown'}</DetailValue>

                  <DetailLabel theme={theme}>Size:</DetailLabel>
                  <DetailValue theme={theme}>{formatBytes(previewFile.fileSize || previewFile.fileSizeBytes || previewFile.size || 0)}</DetailValue>

                  <DetailLabel theme={theme}>Uploaded:</DetailLabel>
                  <DetailValue theme={theme}>{fmtDate(previewFile.createdAt || previewFile.uploadedAt)}</DetailValue>

                  <DetailLabel theme={theme}>By:</DetailLabel>
                  <DetailValue theme={theme}>{previewFile.uploadedByName || previewFile.uploadedBy || previewFile.userName || '--'}</DetailValue>

                  {previewFile.formSubmissionId && (
                    <>
                      <DetailLabel theme={theme}>Submission:</DetailLabel>
                      <DetailValue theme={theme}>
                        <SubmissionLink
                          theme={theme}
                          onClick={(e) => {
                            handleSubmissionClick(previewFile, e);
                            closePreviewModal();
                          }}
                        >
                          #{previewFile.formSubmissionId}
                        </SubmissionLink>
                      </DetailValue>
                    </>
                  )}
                </FileDetails>
              </ModalBody>
              <ModalFooter theme={theme}>
                <Button theme={theme} onClick={closePreviewModal}>Close</Button>
                <Button
                  theme={theme}
                  primary
                  onClick={(e) => handleDownload(previewFile, e)}
                  disabled={downloadingFile === previewFile.id}
                >
                  {downloadingFile === previewFile.id ? 'Downloading...' : 'Download'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        <MarkdownPreviewModal
          isOpen={!!markdownPreview}
          onClose={() => setMarkdownPreview(null)}
          fileUrl={markdownPreview?.fileUrl}
          fileName={markdownPreview?.fileName}
        />
      </PageContainer>
    </>
  );
};

const FileManagerPage = () => (
  <Layout currentPath="/file-manager">
    <FileManagerSection />
  </Layout>
);

export default FileManagerPage;
export { FileManagerPage };
